import { useState } from 'react';

import { useMutation } from '@apollo/client';
import { useQuery } from '@tanstack/react-query';
import { AgGridReact, type AgGridReactProps } from 'ag-grid-react';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { useAppSelector } from '@/app/hooks';
import { addToast } from '@/app/slices/toast';
import {
  type WorkflowTab,
  WorkflowTabBar,
} from '@/components/common/workflow-tab-bar';
import { ToastLife } from '@/components/hoc/toast-container/enums';
import { Button } from '@/components/ui/button';
import { useDefaultGridOptions } from '@/grid';
import { defaultColDef } from '@/grid/defaults';
import { type ColDef, getRowId } from '@/grid/types';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useAccessToken } from '@/services/auth';
import { updateRevisionsBatch } from '@/services/chute-scheduler/api';
import { aspPoolsOptions, poolMultiSiteOptions } from '@/services/opu-search';
import {
  useCreateAspPool,
  useRemoveAspPool,
} from '@/services/opu-search/hooks';
import type {
  AspirationPoolDonorResponse,
  AspirationPoolSummaryResponse,
} from '@/services/opu-search/types';
import { UPDATE_REVISIONS, UpdateRevisionsInput } from '@/types.gql';

type Props = {
  setShowPoolAspirations: React.Dispatch<React.SetStateAction<boolean>>;
  refetch: () => void;
  poolNumArray: number[];
  siteIds: string[];
};

type PoolTab = 'pool' | 'remove';

export type DonorRow = AspirationPoolDonorResponse;
type RemovablePool = AspirationPoolSummaryResponse;

const errorMessage = (error: unknown) =>
  error instanceof Error ? error.message : String(error);

const selectCol = {
  colId: 'select',
  headerName: '',
  checkboxSelection: true,
  headerCheckboxSelection: true,
  headerCheckboxSelectionFilteredOnly: true,
  width: 50,
  maxWidth: 50,
  sortable: false,
  filter: false,
  suppressHeaderMenuButton: true,
  pinned: 'left',
} satisfies ColDef<object>;

// Same column defaults as the main Chute Scheduler grid: filter and sort from
// the column header menu.
const gridDefaultColDef: ColDef<object> = {
  ...defaultColDef,
  flex: 1,
  minWidth: 110,
  headerClass: 'tog-header-black',
};

const donorColumnDefs: ColDef<DonorRow>[] = [
  selectCol,
  { field: 'opuSite', headerName: 'OPU Site' },
  { field: 'donorName', headerName: 'Donor Name', sort: 'asc' },
  { field: 'gradeAbc', headerName: 'Grade ABC', minWidth: 90 },
  { field: 'gradeD', headerName: 'Grade D', minWidth: 90 },
  { field: 'gradeE', headerName: 'Grade E', minWidth: 90 },
  { field: 'sireName', headerName: 'Sire Name' },
  { field: 'semenName', headerName: 'Semen Name' },
  { field: 'semenTypeName', headerName: 'Semen Type' },
];

const poolColumnDefs: ColDef<RemovablePool>[] = [
  selectCol,
  { field: 'name', headerName: 'Name' },
  {
    field: 'poolNumber',
    headerName: 'Pool Number',
    filter: 'agNumberColumnFilter',
    sort: 'asc',
  },
];

export const PoolAspirationsDialog = ({
  setShowPoolAspirations,
  refetch,
  poolNumArray,
  siteIds,
}: Props) => {
  const dispatch = useDispatch();
  const token = useAccessToken();
  const [params] = useSearchParams();
  const aspirationDate = params.get('date') || '';
  const isBatchPoolRevisionEnabled = useFeatureFlag(
    'Batch_Pool_Revision_Comment',
  );

  const [activeTab, setActiveTab] = useState<PoolTab>('pool');
  const [selectedAspirations, setSelectedAspirations] = useState<DonorRow[]>(
    [],
  );
  const [selectedPools, setSelectedPools] = useState<RemovablePool[]>([]);
  const gridOptions = useDefaultGridOptions();
  const { mode } = useAppSelector((s) => s.preferences);
  const gridTheme = `ag-theme-transova${mode === 'dark' ? '-dark' : ''}`;

  // One request for every selected site; each row carries its OPU site.
  const donorsQuery = useQuery(
    poolMultiSiteOptions(token, { aspirationDate, siteIds }),
  );

  const poolsQuery = useQuery({
    ...aspPoolsOptions(token, { poolNumbers: poolNumArray }),
    enabled: !!token && poolNumArray.length > 0,
  });

  const donors: DonorRow[] = donorsQuery.data ?? [];

  const pools: RemovablePool[] = poolsQuery.data ?? [];

  const { mutateAsync: createNewAspirationPool } = useCreateAspPool();
  const { mutateAsync: removeFromAspirationPool } = useRemoveAspPool();
  const [updateRevisions] = useMutation(UPDATE_REVISIONS);

  const refreshAll = () => {
    void donorsQuery.refetch();
    void poolsQuery.refetch();
    refetch();
  };

  const handleRemoveFromPool = async () => {
    const poolNames = selectedPools
      .map((pool) => pool.name ?? `Pool ${pool.poolNumber}`)
      .join(', ');

    try {
      await removeFromAspirationPool({
        aspirationPoolIds: selectedPools.map((pool) => pool.id),
      });
    } catch (error) {
      dispatch(
        addToast({
          severity: 'error',
          summary: 'Could Not Remove From Pool',
          detail: `${poolNames} could not be removed. Please try again. Error: ${errorMessage(error)}`,
          life: ToastLife.LONG,
        }),
      );
      return;
    }

    dispatch(
      addToast({
        severity: 'success',
        summary: 'Removed From Pool',
        detail: `${poolNames} removed.`,
        life: ToastLife.MEDIUM,
      }),
    );
    setShowPoolAspirations(false);
    refreshAll();
  };

  const handlePoolAspirations = async () => {
    if (selectedAspirations.length === 0) return;

    const firstAspiration = selectedAspirations[0];
    const { sireName, semenName, semenTypeName } = firstAspiration;

    const hasMismatch = selectedAspirations.some(
      (aspiration) =>
        aspiration.sireName !== sireName ||
        aspiration.semenName !== semenName ||
        aspiration.semenTypeName !== semenTypeName,
    );

    if (hasMismatch) {
      dispatch(
        addToast({
          severity: 'error',
          summary: 'Cannot Create Pool',
          detail:
            'All selected aspirations must have the same sire, semen name, and semen type.',
          sticky: false,
          closable: true,
        }),
      );
      return;
    }

    let poolResult: string | undefined;
    try {
      poolResult = await createNewAspirationPool({
        aspirationIds: selectedAspirations.map((a) => a.aspirationId),
        siteId: firstAspiration.opuSiteId ?? siteIds[0],
      });
      if (!poolResult) throw new Error('No pool number was returned.');
    } catch (error) {
      dispatch(
        addToast({
          severity: 'error',
          summary: 'Could Not Create Pool',
          detail: `The pool was not created. Please try again. Error: ${errorMessage(error)}`,
          life: ToastLife.LONG,
        }),
      );
      return;
    }

    const revisionComment = `Pool ${poolResult}, sire choice ${sireName}`;
    try {
      if (isBatchPoolRevisionEnabled) {
        await updateRevisionsBatch(token, {
          donorScheduleIds: selectedAspirations.map(
            (a) => a.donorScheduleId ?? '',
          ),
          revisionComment,
        });
      } else {
        await updateRevisions({
          variables: {
            updates: selectedAspirations.map((a) => ({
              donorScheduleId: a.donorScheduleId,
              revisionComment,
            })) as UpdateRevisionsInput[],
          },
        });
      }
      dispatch(
        addToast({
          severity: 'success',
          summary: `Pool ${poolResult} Created`,
          detail: `${selectedAspirations.length} donors pooled.`,
          life: ToastLife.MEDIUM,
        }),
      );
    } catch (error) {
      dispatch(
        addToast({
          severity: 'warn',
          summary: `Pool ${poolResult} Created`,
          detail: `The revision comment could not be added. Add "${revisionComment}" from Revisions. Error: ${errorMessage(error)}`,
          life: ToastLife.LONG,
        }),
      );
    }

    setShowPoolAspirations(false);
    refreshAll();
  };

  const tabs: WorkflowTab<PoolTab>[] = [
    {
      key: 'pool',
      label: 'Pool Aspirations',
      icon: '',
    },
    {
      key: 'remove',
      label: 'Remove from Pool',
      icon: '',
    },
  ];

  const closeButton = (
    <Button variant="outline" onClick={() => setShowPoolAspirations(false)}>
      Close
    </Button>
  );

  return (
    <div className="space-y-4">
      <WorkflowTabBar<PoolTab>
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={(tab) => {
          setSelectedAspirations([]);
          setSelectedPools([]);
          setActiveTab(tab);
        }}
      />

      {activeTab === 'pool' ? (
        <div>
          <div key="pool-grid" className={`h-[50vh] ${gridTheme}`}>
            <AgGridReact
              gridOptions={gridOptions}
              rowData={donors}
              columnDefs={donorColumnDefs as AgGridReactProps['columnDefs']}
              defaultColDef={
                gridDefaultColDef as AgGridReactProps['defaultColDef']
              }
              rowSelection="multiple"
              suppressRowClickSelection
              getRowId={getRowId<DonorRow>((p) => p.data.aspirationId)}
              onSelectionChanged={(e) =>
                setSelectedAspirations(e.api.getSelectedRows() as DonorRow[])
              }
              overlayNoRowsTemplate="No completed, unpooled aspirations for the selected sites."
            />
          </div>
          <div className="mt-4 flex items-center justify-end gap-2">
            <span className="mr-auto text-sm text-gray-500">
              {selectedAspirations.length} selected
            </span>
            {closeButton}
            <Button
              disabled={selectedAspirations.length === 0}
              onClick={handlePoolAspirations}
            >
              Pool
            </Button>
          </div>
        </div>
      ) : (
        <div>
          <div key="remove-grid" className={`h-[50vh] ${gridTheme}`}>
            <AgGridReact
              gridOptions={gridOptions}
              rowData={pools}
              columnDefs={poolColumnDefs as AgGridReactProps['columnDefs']}
              defaultColDef={
                gridDefaultColDef as AgGridReactProps['defaultColDef']
              }
              rowSelection="multiple"
              suppressRowClickSelection
              getRowId={getRowId<RemovablePool>((p) => p.data.id)}
              onSelectionChanged={(e) =>
                setSelectedPools(e.api.getSelectedRows() as RemovablePool[])
              }
              overlayNoRowsTemplate="No pools in the current results."
            />
          </div>
          <div className="mt-4 flex items-center justify-end gap-2">
            <span className="mr-auto text-sm text-gray-500">
              {selectedPools.length} selected
            </span>
            {closeButton}
            <Button
              variant="destructive"
              disabled={selectedPools.length === 0}
              onClick={handleRemoveFromPool}
            >
              Remove from Pool
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
